import { Injectable } from '@angular/core';
import { AppHttpService } from './app-http.service';
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { Account } from 'src/app/shared/models';
import { Ability } from '@casl/ability';
import * as CryptoJS from 'crypto-js';
import * as Sentry from '@sentry/angular';
import { AnalyticsService } from './analytics.service';

export interface SSOConfig {
  sso_flag: boolean;
  sso_type: 'azure' | 'okta';
  sso_url: string;
  sso_client_id: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  redirectUrl: string;
  account: Account;

  /** Emits the fresh account after refreshCurrentUser replaces the cached copy */
  readonly accountRefreshed$ = new Subject<Account>();

  constructor(
    private httpService: AppHttpService,
    private ability: Ability,
    private analyticsService: AnalyticsService,
  ) { }

  /**
   * Fetches current user-info from local storage
   */
  getCurrentUser(): Account {
    try {
      if (!this.account || !this.account.token) {
        const storedUser = JSON.parse(localStorage.getItem('currentUser'));
        // Permission listeners also read the current user. After logout, do
        // not emit another ability update while handling that same update.
        if (!storedUser?.token) {
          this.account = null;
          return null;
        }
        this.account = new Account(storedUser);
        this.updateAbility(this.account.abilities);
      }
      return this.account;
    } catch (e) {
      this.logout();
      return null;
    }
  }

  private generateRandomString(length: number): string {
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    return Array.from(array, (byte) =>
      ('0' + (byte % 36).toString(36)).slice(-1)
    ).join('');
  }

  private generateCodeChallenge(codeVerifier: string): string {
    const hash = CryptoJS.SHA256(codeVerifier);
    return CryptoJS.enc.Base64.stringify(hash)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  /**
   * Initialize SSO login flow with dynamic config from backend
   * @param ssoConfig SSO configuration from backend
   * @param email User email for callback verification
   */
  initializeSSOFlow(ssoConfig: SSOConfig, email: string): string {
    const codeVerifier = this.generateRandomString(128);
    const codeChallenge = this.generateCodeChallenge(codeVerifier);
    const redirectUri = `${window.location.origin}/login`;

    // Store SSO state for callback handling
    sessionStorage.setItem('code_verifier', codeVerifier);
    sessionStorage.setItem('sso_email', email);
    sessionStorage.setItem('sso_type', ssoConfig.sso_type);

    let loginUrl: string;

    if (ssoConfig.sso_type === 'azure') {
      loginUrl =
        `${ssoConfig.sso_url}?client_id=${ssoConfig.sso_client_id}` +
        `&scope=openid profile offline_access` +
        `&redirect_uri=${encodeURIComponent(redirectUri)}` +
        `&state=random_state` +
        `&code_challenge=${codeChallenge}` +
        `&code_challenge_method=S256` +
        `&response_mode=fragment` +
        `&response_type=code` +
        `&prompt=login` +
        `&login_hint=${encodeURIComponent(email)}`;
    } else if (ssoConfig.sso_type === 'okta') {
      loginUrl =
        `${ssoConfig.sso_url}?client_id=${ssoConfig.sso_client_id}` +
        `&scope=openid profile email offline_access` +
        `&redirect_uri=${encodeURIComponent(redirectUri)}` +
        `&state=random_state` +
        `&code_challenge=${codeChallenge}` +
        `&code_challenge_method=S256` +
        `&response_mode=fragment` +
        `&response_type=code` +
        `&login_hint=${encodeURIComponent(email)}`;
    }

    return loginUrl;
  }

  loginWithAzureSSO(code: string) {
    const codeVerifier = sessionStorage.getItem('code_verifier');
    const email = sessionStorage.getItem('sso_email');
    const redirectUri = `${window.location.origin}/login`;
    const payload = { code, redirectUri, codeVerifier, email };

    return this.httpService.postService('/v1/login/azure', payload).pipe(
      map(resp => {
        this.clearSSOSession();
        const user = resp.data;
        user.token = resp.token;
        user.abilities = resp.abilities;
        localStorage.setItem('currentUser', JSON.stringify(user));
        this.updateAbility(resp.abilities);
        Sentry.setUser({ id: user.id });
        this.analyticsService.identify(user, user.organization);
        this.analyticsService.capture('user_logged_in', { method: 'azure_sso', role: user.role });

        return { status: 'success', redirectUrl: this.redirectUrl || this.getDefaultUrl() };
      })
    );
  }

  loginWithOktaSSO(code: string) {
    const codeVerifier = sessionStorage.getItem('code_verifier');
    const email = sessionStorage.getItem('sso_email');
    const redirectUri = `${window.location.origin}/login`;
    const payload = { code, redirectUri, codeVerifier, email };

    return this.httpService.postService('/v1/login/okta', payload).pipe(
      map(resp => {
        this.clearSSOSession();
        const user = resp.data;
        user.token = resp.token;
        user.abilities = resp.abilities;
        localStorage.setItem('currentUser', JSON.stringify(user));
        this.updateAbility(resp.abilities);
        this.analyticsService.identify(user, user.organization);
        this.analyticsService.capture('user_logged_in', { method: 'okta_sso', role: user.role });

        return { status: 'success', redirectUrl: this.redirectUrl || this.getDefaultUrl() };
      })
    );
  }

  /**
   * Clear SSO session storage items
   */
  private clearSSOSession() {
    sessionStorage.removeItem('code_verifier');
    sessionStorage.removeItem('sso_email');
    sessionStorage.removeItem('sso_type');
  }

  /**
   * Get stored SSO type from session
   */
  getSSOType(): string | null {
    return sessionStorage.getItem('sso_type');
  }

  updateAbility(rules = []): void {
    this.ability.update(rules);
  }

  storeFreshAbilities(abilities = []): void {
    const user = this.getCurrentUser();
    if (!user) {
      return;
    }
    user.abilities = abilities;
    localStorage.setItem('currentUser', JSON.stringify(user));
    this.updateAbility(abilities);
  }

  /**
   * Clears current user from local storage
   */
  clearCurrentUser() {
    try {
      localStorage.removeItem('currentUser');
      this.account = null;
    } catch (e) {
      console.error(e);
    }
  }

  /**
   * Returns true if user is logged in.
   * @returns boolean
   */
  isLoggedIn() {
    return 'token' in (this.getCurrentUser() || {});
  }

  /**
   * Refreshes the cached account payload (organization flags, roles, abilities)
   * from the server. The login-time localStorage copy otherwise goes stale for
   * the whole session, so org-level changes would only appear after re-login.
   */
  refreshCurrentUser() {
    const current = this.getCurrentUser();
    if (!current || !current.token) {
      return;
    }
    this.httpService.getService('/v1/login/me').subscribe({
      next: (resp: any) => {
        // Drop a late reply if the session changed in flight (logout, or another
        // account logged in) so it never resurrects or overwrites that session
        if (this.getCurrentUser()?.token !== current.token) {
          return;
        }
        const user = resp.data;
        user.token = current.token;
        user.abilities = resp.abilities;
        this.account = new Account(user);
        localStorage.setItem('currentUser', JSON.stringify(user));
        this.updateAbility(resp.abilities);
        this.accountRefreshed$.next(this.account);
      },
      error: () => {
        // Keep the cached copy. A 401 still logs out via the auth interceptor
      },
    });
  }

  /**
   * Makes login api call to the server
   * @param email login username
   * @param password login password
   */
  login(username: string, password: string) {
    const payload = { username, password };
    return this.httpService.postService('/v1/login', payload).pipe(
      map(resp => {
        const user = resp.data;
        user.token = resp.token;
        user.abilities = resp.abilities;
        localStorage.setItem('currentUser', JSON.stringify(user));
        this.updateAbility(resp.abilities);
        Sentry.setUser({ id: user.id });
        this.analyticsService.identify(user, user.organization);
        this.analyticsService.capture('user_logged_in', { method: 'password', role: user.role });

        return { status: 'success', redirectUrl: this.redirectUrl || this.getDefaultUrl() };
      })
    );
  }

  /**
   * Removes user info from local storage
   */
  logout() {
    return this.httpService.postService('/v1/logout', {}).pipe(
      map(resp => {
        this.clearCurrentUser();
        this.redirectUrl = null;
        this.updateAbility([]);
        Sentry.setUser(null);
        this.analyticsService.reset();
        return { status: 'success' };
      })
    );
  }

  getDefaultUrl() {
    let url = '/';
    const account = this.getCurrentUser();
    if (account.role === 'super-admin') {
      url = '/organization';
    }
    return url;
  }

  /**
   * Fetch acl rules from server and update
   */
  refreshAbilities(): void {
    this.httpService.getService('/v1/appdata').toPromise()
      .then((resp: any) => {
        if (resp?.data?.abilities) {
          this.storeFreshAbilities(resp.data.abilities);
        } else {
          console.error('Failed to fetch acl abilities');
        }
      })
      .catch(e => {
        console.error('Failed to fetch acl abilities', e);
      });
  }

  /**
   * Store updated account details
   * @param account Account
   */
  updateAccountDetails(account: Account) {
    const user = Object.assign(this.getCurrentUser(), account);
    localStorage.setItem('currentUser', JSON.stringify(user));
  }

  /**
   * Check whether user exists
   * @param email login username
   * @param password login password
   */
  checkUser(email: string = null, phone: string = null) {
    const qp = { email, phone };
    return this.httpService.getService('/v1/login/check-user', qp).toPromise();
  }

  /**
   * Requests magic link to this email
   * @param email email
   * @returns {Promise<any>}
   */
  getMagicLink(email: string) {
    const payload = { email };
    return this.httpService.postService('/v1/login/magic', payload).toPromise();
  }

  /**
   * Login with magic link
   * @param email email
   * @returns {Promise<any>}
   */
  loginWithMagicCode(code: string) {
    const payload = { code };
    return this.httpService.postService('/v1/login/magic/verify', payload).toPromise()
      .then((resp: any) => {
        const user = resp.data;
        user.token = resp.token;
        user.abilities = resp.abilities;
        localStorage.setItem('currentUser', JSON.stringify(user));
        this.updateAbility(resp.abilities);
        Sentry.setUser({ id: user.id });
        this.analyticsService.identify(user, user.organization);
        this.analyticsService.capture('user_logged_in', { method: 'magic_link', role: user.role });

        return { status: 'success', redirectUrl: this.redirectUrl || this.getDefaultUrl() };
      });
  }

  /**
   * Request OTP
   * @param phone phone
   * @param otp otp
   */
  requestOTP(phone: string) {
    const payload = { phone };
    return this.httpService.postService('/v1/login/phone', payload).toPromise();
  }

  /**
   * Makes login api with OTP
   * @param phone phone
   * @param otp otp
   */
  loginWithOTP(phone: string, otp: string) {
    const payload = { phone, otp };
    return this.httpService.postService('/v1/login/phone/verify', payload).toPromise()
      .then((resp: any) => {
        const user = resp.data;
        user.token = resp.token;
        user.abilities = resp.abilities;
        localStorage.setItem('currentUser', JSON.stringify(user));
        this.updateAbility(resp.abilities);
        Sentry.setUser({ id: user.id });
        this.analyticsService.identify(user, user.organization);
        this.analyticsService.capture('user_logged_in', { method: 'otp', role: user.role });

        return { status: 'success', redirectUrl: this.redirectUrl || this.getDefaultUrl() };
      });
  }
}
